import {useState, useCallback} from "react";

/**
 * Generic Loading Modal Hook
 * Uzun süren istekler için modal yönetimi sağlar
 * 
 * @returns {Object} Hook döndürülen değerler
 * @returns {boolean} isRequestInProgress - İstek devam ediyor mu?
 * @returns {boolean} modalVisible - Modal görünür mü?
 * @returns {Object} requestData - İstek verisi
 * @returns {Function} showRequestModal - Modal göster
 * @returns {Function} hideRequestModal - Modal gizle
 * @returns {Function} executeWithModal - İstek + modal yönetimi
 */
export const useLoadingModal = () => {
    const [isRequestInProgress, setIsRequestInProgress] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [requestData, setRequestData] = useState(null);

    const showRequestModal = useCallback((data) => {
        setRequestData(data);
        setModalVisible(true);
        setIsRequestInProgress(true);
    }, []);

    const hideRequestModal = useCallback(() => {
        setModalVisible(false);
        setIsRequestInProgress(false);
        setRequestData(null);
    }, []);

    /**
     * İstek + modal yönetimi için generic fonksiyon
     * 
     * @param {Function} requestFn - Çalıştırılacak istek fonksiyonu
     * @param {Object} modalConfig - Modal konfigürasyonu
     * @param {string} modalConfig.title - Modal başlığı
     * @param {string} modalConfig.message - Ana mesaj
     * @param {string} modalConfig.subMessage - Alt mesaj
     * @param {Object} modalConfig.icon - İkon bileşeni
     * @param {string} modalConfig.iconColor - İkon rengi
     * @param {boolean} modalConfig.showSpinner - Spinner göster
     * @param {string} modalConfig.spinnerText - Spinner metni
     * @returns {Promise} İstek sonucu
     */
    const executeWithModal = useCallback(async (requestFn, modalConfig = {}) => {
        try {
            // Modal'ı göster
            showRequestModal(modalConfig);
            
            // İsteği çalıştır
            const result = await requestFn();
            
            // Başarılı olursa modal'ı kapat
            hideRequestModal();
            
            return result;
        } catch (error) {
            // Hata olursa modal'ı kapat
            hideRequestModal();
            throw error;
        }
    }, [showRequestModal, hideRequestModal]);

    return {
        isRequestInProgress,
        modalVisible,
        requestData,
        showRequestModal,
        hideRequestModal,
        executeWithModal
    };
};
